<template>
  <div class="event-report-container">
    <!-- 페이지 타이틀 -->
    <div class="page-header">
      <h2>📊 이벤트 리포트</h2>
      <p>고객 세그먼트별 성과와 전환 퍼널을 한눈에 확인하세요.</p>
      <div class="segment-info">
        <span class="segment-label">세그먼트:</span>
        <div class="segment-badge certificate">👥 인증서 보유자</div>
        <span class="segment-separator">›</span>
        <div class="segment-badge review">✍️ 후기 작성</div>
        <span class="segment-separator">+</span>
        <div class="segment-badge viral">💬 바이럴</div>
        <span class="segment-divider">|</span>
        <div class="segment-badge new-customer">🎯 신규 고객</div>
        <span class="segment-separator">›</span>
        <div class="segment-badge reservation">📅 예약 전환</div>
      </div>
    </div>

    <!-- 이벤트 선택 (항상 표시) -->
    <div class="event-selector-panel">
      <div class="event-selector-content">
        <span class="selector-label">이벤트 선택:</span>
        <a-select
          v-model:value="eventIds"
          placeholder="이벤트를 선택해주세요"
          class="event-selector"
          :loading="loadingEvents"
          @change="handleEventChange"
          allow-clear
        >
          <a-select-option
            v-for="event in eventList"
            :key="event._id"
            :value="event._id"
          >
            {{ event.eventTitle }} ({{ formatDate(event.startDate) }} ~ {{ formatDate(event.endDate) }})
          </a-select-option>
        </a-select>
        <a-button @click="handleRefresh" :loading="refreshing" :disabled="!eventIds">
          <template #icon><ReloadOutlined /></template>
          새로고침
        </a-button>
      </div>
    </div>

    <!-- 이벤트 미선택 시: 기본 HTML 리포트 또는 안내 -->
    <div v-if="!eventIds">
      <div v-if="loadingDefaultHtml" class="no-event-selected">
        <a-spin size="large" />
      </div>
      <iframe
        v-else-if="defaultHtmlContent"
        :srcdoc="defaultHtmlContent"
        class="default-html-frame"
        sandbox="allow-same-origin"
      />
      <div v-else class="no-event-selected">
        <div class="no-event-content">
          <span class="no-event-icon">📊</span>
          <h3>이벤트를 선택해주세요</h3>
          <p>리포트를 조회할 이벤트를 상단에서 선택해주세요.</p>
        </div>
      </div>
    </div>

    <!-- HTML 렌더 리포트 이벤트 선택 시 (자이스 이벤트 등) -->
    <div v-else-if="isHtmlReportEvent">
      <div v-if="loadingDefaultHtml" class="no-event-selected">
        <a-spin size="large" />
      </div>
      <iframe
        v-else-if="defaultHtmlContent"
        :srcdoc="defaultHtmlContent"
        class="default-html-frame"
        sandbox="allow-same-origin"
      />
    </div>

    <!-- 조회 결과 -->
    <a-tabs v-else v-model:activeKey="activeTab" type="card" class="results-section">
      <a-tab-pane key="overview" tab="📊 전체 개요">
        <OverviewSummary
          :daily-message-conversions="reportData.dailyMessageConversions"
          :daily-conversions="reportData.dailyConversions"
          :cumulative-summaries="reportData.cumulativeSummaries"
          :overall-summary="reportData.overallSummary"
          :summary="reportData.summary"
          :report-id="reportData.reportId"
        />
      </a-tab-pane>

      <a-tab-pane key="messageConversion" tab="📬 메시지 유형별 전환">
        <MessageConversionReport
          :report-data="reportData.dailyMessageConversions"
        />
      </a-tab-pane>

      <a-tab-pane key="groupAnalysis" tab="📊 그룹별 분석">
        <a-tabs v-model:activeKey="activeGroupTab" class="group-analysis-sub-tabs">
          <a-tab-pane key="groupA" tab="그룹A">
            <GroupADetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
          <a-tab-pane key="groupB" tab="그룹B">
            <GroupBDetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
          <a-tab-pane key="groupC" tab="그룹C">
            <GroupCDetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
          <a-tab-pane key="groupD" tab="그룹D">
            <GroupDDetails
              v-if="reportData.reportId"
              :report-id="reportData.reportId"
            />
            <div v-else class="empty-content">
              <p>보고서를 조회해주세요</p>
            </div>
          </a-tab-pane>
        </a-tabs>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import { ref, reactive, computed, getCurrentInstance, onMounted } from 'vue';

import { Tabs, Space, Button, DatePicker, Select, Spin, message } from 'ant-design-vue';
import { ReloadOutlined } from '@ant-design/icons-vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';
import OverviewSummary from './components/OverviewSummary.vue';
import CertificateHolderReport from './components/CertificateHolderReport.vue';
import NonCertificateReport from './components/NonCertificateReport.vue';
import MessageConversionReport from './components/MessageConversionReport.vue';
import GroupADetails from './components/GroupADetails.vue';
import GroupBDetails from './components/GroupBDetails.vue';
import GroupCDetails from './components/GroupCDetails.vue';
import GroupDDetails from './components/GroupDDetails.vue';
import reportApi from './api';
import eventsApi from '../api';

export default {
  name: 'EventReport',
  components: {
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
    ASpace: Space,
    AButton: Button,
    ARangePicker: DatePicker.RangePicker,
    ASelect: Select,
    ASelectOption: Select.Option,
    ASpin: Spin,
    OverviewSummary,
    CertificateHolderReport,
    NonCertificateReport,
    MessageConversionReport,
    GroupADetails,
    GroupBDetails,
    GroupCDetails,
    GroupDDetails,
    ReloadOutlined,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const instance = getCurrentInstance();
    const activeTab = ref('overview');
    const activeGroupTab = ref('groupA');
    const overviewSubTab = ref('summary');
    const refreshing = ref(false);
    const loadingEvents = ref(false);
    const loadingDefaultHtml = ref(false);
    const defaultHtmlContent = ref('');
    const eventList = ref([]);

    // URL 쿼리 파라미터에서 초기값 읽기
    const queryEventId = route.query.eventId || route.params.eventId;
    const queryStartDate = route.query.startDate;
    const queryEndDate = route.query.endDate;

    // eventIds 초기값 설정 (URL에서 eventId가 있으면 사용) - 단일 선택
    const eventIds = ref(queryEventId || null);

    // dateRange 초기값 설정 (URL에서 날짜가 있으면 사용)
    const dateRange = ref(
      queryStartDate && queryEndDate
        ? [dayjs(queryStartDate), dayjs(queryEndDate)]
        : [dayjs().subtract(7, 'day'), dayjs()]
    );

    const reportData = reactive({
      dailyMessageConversions: [],
      dailyConversions: [],
      cumulativeSummaries: [],
      overallSummary: null,
      summary: null,
      reportId: null
    });

    // 선택된 이벤트 정보 저장
    const selectedEvent = ref(null);

    // 선택된 이벤트가 HTML 렌더 리포트를 사용하는 이벤트인지 여부 (이름에 '자이스' 포함)
    const isHtmlReportEvent = computed(() => {
      if (!selectedEvent.value) return false;
      return selectedEvent.value.eventTitle?.includes('자이스');
    });

    // API 인스턴스 생성
    const api = reportApi(instance.proxy.$apiInstance, store);
    const eventsApiInstance = eventsApi(instance.proxy.$apiInstance, store);

    // 기본 HTML 리포트 조회 (이벤트 미선택 시 표시)
    const fetchDefaultHtml = async () => {
      loadingDefaultHtml.value = true;
      try {
        const listResp = await api.fetchHtmlReportList();
        const files = listResp?.data || [];
        console.log('[EventReport] HTML report list:', files.length, 'files');
        if (files.length === 0) return;
        const latest = files[0].filename;
        console.log('[EventReport] Loading default HTML:', latest);
        const html = await api.fetchHtmlReportContent(latest);
        defaultHtmlContent.value = html;
        console.log('[EventReport] Default HTML loaded, length:', html?.length);
      } catch (error) {
        console.error('[EventReport] Failed to load default HTML report:', error);
      } finally {
        loadingDefaultHtml.value = false;
      }
    };

    // 이벤트 목록 조회 (레포트 노출이 true인 이벤트만)
    const fetchEventList = async () => {
      loadingEvents.value = true;
      try {
        await eventsApiInstance.getEvents(
          { status: 'all', page: 1, limit: 1000, showInReport: true },
          (response) => {
            const events = response.data?.items || response.data?.data || [];
            eventList.value = Array.isArray(events) ? events : [];

            if (eventList.value.length > 0) {
              // URL 파라미터로 eventId가 있으면 해당 이벤트 찾기
              if (queryEventId) {
                const foundEvent = eventList.value.find(e => e._id === queryEventId);
                if (foundEvent) {
                  selectedEvent.value = foundEvent;
                  // URL에 날짜 파라미터가 없으면 이벤트의 기간 사용
                  if (!queryStartDate || !queryEndDate) {
                    if (foundEvent.startDate && foundEvent.endDate) {
                      dateRange.value = [
                        dayjs(foundEvent.startDate),
                        dayjs(foundEvent.endDate)
                      ];
                    }
                  }
                }
              }
              // URL 파라미터가 없으면 자이스 이벤트 자동 선택 (없으면 사용자가 직접 선택)
              if (!queryEventId) {
                const zeissEvent = eventList.value.find(e => e.eventTitle?.includes('자이스'));
                if (zeissEvent) {
                  eventIds.value = zeissEvent._id;
                  selectedEvent.value = zeissEvent;
                  if (zeissEvent.startDate && zeissEvent.endDate) {
                    dateRange.value = [
                      dayjs(zeissEvent.startDate),
                      dayjs(zeissEvent.endDate)
                    ];
                  }
                }
              }
            }
          },
          (error) => {
            console.error('Failed to fetch event list:', error);
            message.error('이벤트 목록 조회에 실패했습니다.');
          }
        );
      } catch (error) {
        console.error('Error fetching event list:', error);
      } finally {
        loadingEvents.value = false;
      }
    };

    // 이벤트 변경 핸들러
    const handleEventChange = (value) => {
      eventIds.value = value;

      // 선택된 이벤트의 기간으로 날짜 범위 자동 변경
      if (value) {
        const event = eventList.value.find(e => e._id === value);
        if (event) {
          selectedEvent.value = event;
          if (event.startDate && event.endDate) {
            dateRange.value = [
              dayjs(event.startDate),
              dayjs(event.endDate)
            ];
          }
        }
      } else {
        selectedEvent.value = null;
      }

      // 이벤트 변경 시 자동으로 레포트 조회
      if (value) {
        fetchReportData();
      }
    };

    // 보고서 데이터 조회
    const fetchReportData = async () => {
      if (!eventIds.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      // HTML 렌더 리포트 이벤트는 API 조회 불필요
      if (isHtmlReportEvent.value) return;

      if (!dateRange.value || dateRange.value.length !== 2) {
        message.warning('날짜 범위를 선택해주세요.');
        return;
      }

      refreshing.value = true;
      try {
        const params = {
          eventId: eventIds.value,
          startDate: dateRange.value[0].format('YYYY-MM-DD'),
          endDate: dateRange.value[1].format('YYYY-MM-DD')
        };

        await api.getReport(
          params,
          (response) => {
            if (response.data) {
              console.log('🔍 [API Response] Full response.data:', response.data);
              console.log('🔍 [API Response] summary:', response.data.summary);
              console.log('🔍 [API Response] summary.hospitalReservationStats:', response.data.summary?.hospitalReservationStats);

              // 레포트 ID 저장 (그룹 상세 조회에 사용)
              reportData.reportId = response.data._id || null;
              console.log('📝 [reportData] reportId 저장:', reportData.reportId);

              reportData.dailyMessageConversions = response.data.dailyMessageConversions || [];
              reportData.dailyConversions = response.data.dailyConversions || [];
              reportData.cumulativeSummaries = response.data.cumulativeSummaries || [];
              reportData.overallSummary = response.data.overallSummary || null;
              reportData.summary = response.data.summary || null;

              console.log('🔍 [reportData] summary after assignment:', reportData.summary);
              console.log('🔍 [reportData] summary.hospitalReservationStats:', reportData.summary?.hospitalReservationStats);

              message.success('보고서를 조회했습니다.');
            }
          },
          (error) => {
            console.error('Failed to fetch report:', error);
            // 404 에러인 경우 워닝 메시지로 안내
            if (error.response?.status === 404) {
              message.warning('보고서를 찾을 수 없습니다. "레포트 생성" 버튼을 클릭하여 보고서를 생성해주세요.');
            } else {
              message.error('보고서 조회에 실패했습니다.');
            }
          }
        );
      } catch (error) {
        console.error('Error fetching report:', error);
      } finally {
        refreshing.value = false;
      }
    };

    // 새로고침
    const handleRefresh = () => {
      fetchReportData();
    };

    // 날짜 범위 변경 핸들러
    const handleDateRangeChange = (dates) => {
      if (dates && dates.length === 2) {
        fetchReportData();
      }
    };

    // 날짜 포맷 헬퍼 함수
    const formatDate = (dateString) => {
      if (!dateString) return '';
      return dayjs(dateString).format('YYYY-MM-DD');
    };

    // 페이지 진입 시 이벤트 목록 조회 후 자동으로 데이터 조회
    onMounted(async () => {
      await Promise.all([fetchEventList(), fetchDefaultHtml()]);
      if (eventIds.value) {
        fetchReportData();
      }
    });

    return {
      activeTab,
      activeGroupTab,
      overviewSubTab,
      eventIds,
      eventList,
      dateRange,
      reportData,
      refreshing,
      loadingEvents,
      loadingDefaultHtml,
      defaultHtmlContent,
      isHtmlReportEvent,
      handleEventChange,
      handleRefresh,
      handleDateRangeChange,
      formatDate,
    };
  },
};
</script>

<style scoped>
.event-report-container {
  padding: 24px;
  background: #fff;
  min-height: calc(100vh - 64px);
}

/* 페이지 타이틀 */
.page-header {
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E9ECEF;
}

.page-header h2 {
  font-size: 26px;
  font-weight: 700;
  color: #212529;
  margin: 0 10px 6px 0;
}

.page-header p {
  font-size: 14px;
  color: #6C757D;
  margin: 0 10px 0 0;
  line-height: 1.5;
}

.segment-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 0;
}

.segment-label {
  font-size: 13px;
  color: #495057;
  font-weight: 500;
  margin-right: 4px;
}

.segment-badge {
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.segment-badge.certificate {
  background: #E7F1FF;
  color: #0A58CA;
}

.segment-badge.review,
.segment-badge.reservation {
  background: #E9FBEF;
  color: #198754;
}

.segment-badge.viral,
.segment-badge.new-customer {
  background: #FFF4E6;
  color: #D97706;
}

.segment-separator {
  color: #CED4DA;
  margin: 0 2px;
  font-size: 14px;
  font-weight: 600;
}

.segment-divider {
  color: #CED4DA;
  margin: 0 6px;
  font-size: 14px;
}

/* 이벤트 선택 패널 */
.event-selector-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.event-selector-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.selector-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.event-selector {
  flex: 1;
  min-width: 350px;
  max-width: 600px;
}

/* 기본 HTML 리포트 iframe */
.default-html-frame {
  width: 100%;
  height: calc(100vh - 220px);
  border: none;
  border-radius: 8px;
  display: block;
  margin-top: 8px;
}

/* 이벤트 미선택 안내 */
.no-event-selected {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #fafafa;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  margin-top: 20px;
}

.no-event-content {
  text-align: center;
  padding: 40px;
}

.no-event-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
}

.no-event-content h3 {
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.no-event-content p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* 조회 결과 */
.results-section {
  background: #fff;
}

:deep(.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab) {
  padding: 12px 20px;
  font-weight: 500;
}

:deep(.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active) {
  border-bottom-color: #fff;
}

:deep(.ant-tabs-content) {
  background: #fff;
}

/* 전체 개요 하위 탭 */
.overview-sub-tabs {
  margin-top: 0;
  padding-top: 16px;
}

:deep(.overview-sub-tabs .ant-tabs-nav) {
  margin-bottom: 20px;
  background: #fafafa;
  padding: 12px 16px 0;
  border-radius: 8px 8px 0 0;
}

:deep(.overview-sub-tabs .ant-tabs-tab) {
  padding: 10px 20px;
  font-weight: 500;
}

:deep(.overview-sub-tabs .ant-tabs-tab-active) {
  background: #fff;
  border-radius: 6px 6px 0 0;
}

/* 그룹별 분석 하위 탭 */
.group-analysis-sub-tabs {
  margin-top: 0;
  padding-top: 16px;
}

:deep(.group-analysis-sub-tabs .ant-tabs-nav) {
  margin-bottom: 20px;
  background: #fafafa;
  padding: 12px 16px 0;
  border-radius: 8px 8px 0 0;
}

:deep(.group-analysis-sub-tabs .ant-tabs-tab) {
  padding: 10px 20px;
  font-weight: 500;
}

:deep(.group-analysis-sub-tabs .ant-tabs-tab-active) {
  background: #fff;
  border-radius: 6px 6px 0 0;
}

/* 빈 콘텐츠 영역 */
.empty-content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  margin: 20px;
}

.empty-content p {
  font-size: 16px;
  color: #8c8c8c;
  margin: 0;
}
</style>
